/**
 * Dierema wishlist (favorites) system.
 *
 * Self-contained, isolated feature: does not depend on the theme's internal
 * `Component` base class or event-delegation system, so it can be removed by
 * deleting this file plus the other `dr-wishlist-*` files without touching
 * anything else.
 *
 * Storage: localStorage only, keyed by product handle (stable, human
 * readable, and it is exactly what we need to re-fetch real product data on
 * the favorites page via `/products/{handle}.js`).
 *
 * This single module also renders the favorites page (see the
 * `initWishlistPage` section below) so the feature stays inside one asset
 * file, loaded globally like every other theme script.
 */

// Ported from the Horizon theme, which resolved '@theme/money-formatting' through an import map.
// This theme loads the file as a classic script (no import map, no type="module"), where an
// import statement is a SyntaxError that stopped the whole feature from running. Prices are
// formatted the way the theme's own JS does it (Alpine main store), with a plain Intl fallback.
function formatMoney(cents, _moneyFormat, currency) {
  try {
    const store = window.Alpine && window.Alpine.store ? window.Alpine.store('main') : null;
    if (store && typeof store.getPrice === 'function') return store.getPrice(cents);
  } catch (error) {
    /* fall through to Intl */
  }
  const locale = (window.Shopify && window.Shopify.locale) || 'fr';
  const code = currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  return new Intl.NumberFormat(locale, { style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 2 }).format((Number(cents) || 0) / 100);
}

const STORAGE_KEY = 'dierema:wishlist';
const CHANGE_EVENT = 'dr-wishlist:change';

/** @returns {string[]} Array of favorited product handles, de-duplicated. */
function getFavorites() {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((handle) => typeof handle === 'string' && handle.length > 0);
  } catch (error) {
    console.warn('[dr-wishlist] Could not read favorites from localStorage.', error);
    return [];
  }
}

/** @param {string[]} handles */
function saveFavorites(handles) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(handles));
  } catch (error) {
    console.warn('[dr-wishlist] Could not save favorites to localStorage.', error);
  }
}

/**
 * @param {string} handle
 * @returns {boolean}
 */
function isFavorite(handle) {
  if (!handle) return false;
  return getFavorites().includes(handle);
}

/**
 * Adds or removes a product handle from the favorites list and notifies
 * every listener on the page (other buttons for the same product, the
 * favorites page, etc).
 *
 * @param {string} handle
 * @returns {boolean} The new favorited state.
 */
function toggleFavorite(handle) {
  if (!handle) return false;

  const current = getFavorites();
  const index = current.indexOf(handle);
  let favorited;

  if (index === -1) {
    current.push(handle);
    favorited = true;
  } else {
    current.splice(index, 1);
    favorited = false;
  }

  saveFavorites(current);

  document.dispatchEvent(
    new CustomEvent(CHANGE_EVENT, {
      detail: { handle, favorited },
    })
  );

  return favorited;
}

/**
 * @param {string} handle
 */
function removeFavorite(handle) {
  if (!isFavorite(handle)) return;
  toggleFavorite(handle);
}

/**
 * Heart toggle button. Renders its two icon states via CSS (see
 * dr-wishlist-button-styles.liquid) and keeps itself in sync with any other
 * instance of the same product handle already on the page.
 */
class DrWishlistButton extends HTMLElement {
  connectedCallback() {
    this.handle = this.getAttribute('data-product-handle') || '';
    this.button = this.querySelector('.dr-wishlist-button__control');

    if (!this.handle || !this.button) return;

    this._onClick = (event) => {
      event.preventDefault();
      const favorited = toggleFavorite(this.handle);
      this.#render(favorited);
    };

    this._onChange = (event) => {
      if (event.detail?.handle !== this.handle) return;
      this.#render(event.detail.favorited);
    };

    this._onStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#render(isFavorite(this.handle));
    };

    this.button.addEventListener('click', this._onClick);
    document.addEventListener(CHANGE_EVENT, this._onChange);
    window.addEventListener('storage', this._onStorage);

    this.#render(isFavorite(this.handle));
  }

  disconnectedCallback() {
    if (this.button && this._onClick) this.button.removeEventListener('click', this._onClick);
    if (this._onChange) document.removeEventListener(CHANGE_EVENT, this._onChange);
    if (this._onStorage) window.removeEventListener('storage', this._onStorage);
  }

  /** @param {boolean} favorited */
  #render(favorited) {
    if (!this.button) return;
    this.button.setAttribute('aria-pressed', String(favorited));
    this.button.setAttribute(
      'aria-label',
      favorited ? this.button.getAttribute('data-label-remove') || '' : this.button.getAttribute('data-label-add') || ''
    );
  }
}

if (!customElements.get('dr-wishlist-button')) {
  customElements.define('dr-wishlist-button', DrWishlistButton);
}

/**
 * Header wishlist indicator. Read-only: reflects whether the wishlist has
 * at least one item, it does not add/remove anything itself. Reuses the
 * exact same storage (getFavorites) and sync signals (CHANGE_EVENT,
 * storage) as DrWishlistButton above - no separate state, no new storage.
 */
class DrWishlistIndicator extends HTMLElement {
  connectedCallback() {
    this._onChange = () => this.#render();
    this._onStorage = (event) => {
      if (event.key !== STORAGE_KEY) return;
      this.#render();
    };

    document.addEventListener(CHANGE_EVENT, this._onChange);
    window.addEventListener('storage', this._onStorage);

    this.#render();
  }

  disconnectedCallback() {
    if (this._onChange) document.removeEventListener(CHANGE_EVENT, this._onChange);
    if (this._onStorage) window.removeEventListener('storage', this._onStorage);
  }

  #render() {
    this.toggleAttribute('data-active', getFavorites().length > 0);
  }
}

if (!customElements.get('dr-wishlist-indicator')) {
  customElements.define('dr-wishlist-indicator', DrWishlistIndicator);
}

/**
 * Escapes a string for safe insertion into innerHTML.
 * @param {string} value
 * @returns {string}
 */
function escapeHtml(value) {
  const div = document.createElement('div');
  div.textContent = value ?? '';
  return div.innerHTML;
}

/**
 * Builds one favorites-page card from real product data returned by
 * `/products/{handle}.js`. No field is invented: title, price, image and
 * url all come straight from Shopify.
 *
 * @param {object} product
 * @param {{moneyFormat: string, currency: string, removeLabel: string, handle: string, onRemove: () => void}} options
 */
function buildWishlistCard(product, { moneyFormat, currency, removeLabel, onRemove }) {
  const card = document.createElement('div');
  card.className = 'dr-wishlist-page__card';
  card.setAttribute('role', 'listitem');

  const image = product.featured_image || (Array.isArray(product.images) ? product.images[0] : '');
  const price = formatMoney(product.price, moneyFormat, currency);
  const title = escapeHtml(product.title || '');
  const url = product.url || '';

  card.innerHTML = `
    <a class="dr-wishlist-page__card-media" href="${url}">
      ${image ? `<img src="${image}" alt="${title}" loading="lazy" width="600" height="750">` : ''}
    </a>
    <h2 class="dr-wishlist-page__card-title"><a href="${url}">${title}</a></h2>
    <p class="dr-wishlist-page__card-price">${price}</p>
    <button type="button" class="dr-wishlist-page__card-remove">${escapeHtml(removeLabel)}</button>
  `;

  const removeButton = card.querySelector('.dr-wishlist-page__card-remove');
  if (removeButton) {
    removeButton.addEventListener('click', () => {
      card.remove();
      onRemove();
    });
  }

  return card;
}

/**
 * Renders the favorites page, if present on the current page.
 * Fetches each favorited product's real data from Shopify; products that
 * no longer exist (deleted, unpublished) are silently dropped from the
 * list and from storage instead of breaking the page.
 */
function initWishlistPage() {
  const root = document.querySelector('[data-dr-wishlist-page]');
  if (!root) return;

  const listEl = root.querySelector('[data-dr-wishlist-list]');
  const emptyEl = root.querySelector('[data-dr-wishlist-empty]');
  if (!listEl || !emptyEl) return;

  const moneyFormat = root.getAttribute('data-money-format') || '{{ amount }}';
  const currency = root.getAttribute('data-currency') || 'EUR';
  const removeLabel = root.getAttribute('data-remove-label') || 'Retirer';

  const handles = getFavorites();

  const updateEmptyState = () => {
    emptyEl.hidden = listEl.children.length > 0;
  };

  if (handles.length === 0) {
    updateEmptyState();
    return;
  }

  handles.forEach((handle) => {
    fetch(`/products/${encodeURIComponent(handle)}.js`)
      .then((response) => {
        if (!response.ok) throw new Error('Product not found: ' + handle);
        return response.json();
      })
      .then((product) => {
        const card = buildWishlistCard(product, {
          moneyFormat,
          currency,
          removeLabel,
          handle,
          onRemove: () => {
            removeFavorite(handle);
            updateEmptyState();
          },
        });
        listEl.appendChild(card);
        updateEmptyState();
      })
      .catch((error) => {
        console.warn('[dr-wishlist] ' + error.message);
        removeFavorite(handle);
        updateEmptyState();
      });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initWishlistPage);
} else {
  initWishlistPage();
}
